import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

// Iter 63 — Support tickets (v1). Reused by PlayerDashboard, StreamerDashboard,
// and AdminDashboard. Passing `admin` flips the list scope (user sees own,
// admin sees all) and hides the "new ticket" affordance for admin sessions.
//
// Iter 145 — Exposes `window.__vgOpenTicketPrefill({subject, body})` while
// mounted so cross-tab flows (e.g. BuyCredits' "Open a ticket" on a
// rejected top-up row) can open the create modal pre-populated with row
// context. Uses the SAME POST /api/tickets path — no parallel endpoint,
// no parallel form. Cleanup on unmount so we never leak stale handlers.
//
// Iter 197 — All hardcoded strings routed through i18n (en/de/es).
export default function SupportTickets({ admin = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [openThread, setOpenThread] = useState(null); // {ticket, messages}
  const [reply, setReply] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErrorMessage(e, t("support.toast.loadFailed"))); }
    finally { setLoading(false); }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (admin) return;
    window.__vgOpenTicketPrefill = ({ subject = "", body = "" } = {}) => {
      setNewSubject(String(subject).slice(0, 200));
      setNewBody(String(body).slice(0, 5000));
      setNewOpen(true);
    };
    return () => { if (window.__vgOpenTicketPrefill) delete window.__vgOpenTicketPrefill; };
  }, [admin]);

  const createTicket = async () => {
    if ((newSubject || "").trim().length < 2 || !(newBody || "").trim()) { toast.error(t("support.toast.subjectMsgReq")); return; }
    setBusy(true);
    try {
      await api.post("/tickets", { subject: newSubject.trim(), body: newBody.trim() });
      toast.success(t("support.toast.ticketOpened"));
      setNewOpen(false); setNewSubject(""); setNewBody("");
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.field === "ticketBody") toast.error(det.message || t("support.toast.msgBlocked"));
      else toast.error(apiErrorMessage(e, t("support.toast.openFailed")));
    } finally { setBusy(false); }
  };

  const openTicket = async (tid) => {
    try {
      const { data } = await api.get(`/tickets/${tid}`);
      setOpenThread(data);
      setReply("");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const postReply = async () => {
    if (!openThread || !(reply || "").trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${openThread.ticket.id}/messages`, { body: reply.trim() });
      const { data } = await api.get(`/tickets/${openThread.ticket.id}`);
      setOpenThread(data);
      setReply("");
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.field === "ticketBody") toast.error(det.message || t("support.toast.msgBlocked"));
      else toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };
  const closeCurrent = async () => {
    if (!openThread) return;
    if (!window.confirm(t("support.toast.confirmClose"))) return;
    setBusy(true);
    try {
      await api.patch(`/tickets/${openThread.ticket.id}/close`);
      toast.success(t("support.toast.ticketClosed"));
      const { data } = await api.get(`/tickets/${openThread.ticket.id}`);
      setOpenThread(data); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const senderLabel = (m) => {
    if (m.senderType === "admin") return t("support.thread.sender.vg");
    if (admin) return t("support.thread.sender.user");
    return t("support.thread.sender.you");
  };
  const isMine = (m) => (admin ? m.senderType === "admin" : m.senderType !== "admin");

  return (
    <section data-testid={admin ? "admin-tickets-panel" : "support-tickets-panel"} className="mt-10">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#2DD4A7]">{admin ? t("support.eyebrowAdmin") : t("support.eyebrow")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tighter">{admin ? t("support.titleAdmin") : t("support.title")}</h2>
        </div>
        {!admin && (
          <Button data-testid="support-new-ticket-btn" onClick={() => setNewOpen(true)} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] h-10 font-semibold">{t("support.openBtn")}</Button>
        )}
      </div>

      <div className="border border-white/10">
        <table className="w-full text-sm" data-testid={admin ? "admin-tickets-table" : "support-tickets-table"}>
          <thead className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">
            <tr className="border-b border-white/10">
              {admin && <th className="text-left p-3">{t("support.col.opener")}</th>}
              <th className="text-left p-3">{t("support.col.subject")}</th>
              <th className="text-left p-3">{t("support.col.status")}</th>
              <th className="text-left p-3">{t("support.col.lastActivity")}</th>
              <th className="text-left p-3">{t("support.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={admin ? 5 : 4} className="p-4 text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("support.loading")}</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={admin ? 5 : 4} className="p-4 text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]" data-testid="support-tickets-empty">{t("support.empty")}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-white/5" data-testid={`ticket-row-${r.id}`}>
                {admin && <td className="p-3"><div className="font-medium">{r.openerLabel || "—"}</div><div className="text-[10px] font-mono text-[#8B97AE]">{r.openerType} · {r.openerEmail || ""}</div></td>}
                <td className="p-3">{r.subject}</td>
                <td className="p-3">
                  <span data-testid={`ticket-status-${r.id}`} className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${r.status === "open" ? "border-[#2DD4A7]/50 text-[#2DD4A7]" : "border-white/20 text-[#8B97AE]"}`}>{r.status}</span>
                </td>
                <td className="p-3 text-[11px] font-mono text-[#8B97AE]">{(r.lastMessageAt || r.updatedAt || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-3"><button data-testid={`ticket-open-${r.id}`} onClick={() => openTicket(r.id)} className="text-[#2DD4A7] hover:text-white text-xs font-mono uppercase tracking-widest">{t("support.rowOpen")}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {newOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="support-new-modal" onClick={() => setNewOpen(false)}>
          <div className="bg-[#0B0E14] border border-white/15 max-w-xl w-full mt-16" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="font-display text-xl font-black tracking-tighter">{t("support.modal.title")}</div>
              <button onClick={() => setNewOpen(false)} className="text-[#8B97AE] hover:text-white text-xs font-mono uppercase tracking-widest">{t("support.modal.close")}</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("support.modal.subjectLabel")}</Label>
                <Input data-testid="support-new-subject" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} className="mt-1 rounded-none bg-transparent border-white/20 h-10" placeholder={t("support.modal.subjectPh")} />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("support.modal.messageLabel")}</Label>
                <textarea data-testid="support-new-body" value={newBody} onChange={(e) => setNewBody(e.target.value)} rows={5} className="mt-1 w-full rounded-none bg-transparent border border-white/20 text-sm p-3" placeholder={t("support.modal.messagePh")} />
                <div className="text-[10px] font-mono text-[#8B97AE] mt-1">{t("support.modal.hint")}</div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button data-testid="support-new-submit" onClick={createTicket} disabled={busy} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] h-10 font-semibold disabled:opacity-40">{busy ? "…" : t("support.modal.submit")}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {openThread && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-6 overflow-y-auto" data-testid="support-thread-modal" onClick={() => setOpenThread(null)}>
          <div className="bg-[#0B0E14] border border-white/15 max-w-2xl w-full mt-10" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B97AE]">{t("support.thread.eyebrow")} · {openThread.ticket.status}</div>
                <div className="font-display text-xl font-black tracking-tighter" data-testid="thread-subject">{openThread.ticket.subject}</div>
                {admin && <div className="text-[10px] font-mono text-[#8B97AE] mt-1">{t("support.opener.by")}: {openThread.ticket.openerLabel || "—"} · {openThread.ticket.openerType} · {openThread.ticket.openerEmail}</div>}
              </div>
              <div className="flex items-center gap-2">
                {openThread.ticket.status === "open" && (
                  <button data-testid="thread-close-btn" onClick={closeCurrent} disabled={busy} className="text-[#F26D6D] hover:text-white text-xs font-mono uppercase tracking-widest">{t("support.thread.closeTicket")}</button>
                )}
                <button onClick={() => setOpenThread(null)} className="text-[#8B97AE] hover:text-white text-xs font-mono uppercase tracking-widest">{t("support.thread.close")}</button>
              </div>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto" data-testid="thread-messages">
              {openThread.messages.map((m) => (
                <div key={m.id} data-testid={`thread-msg-${m.id}`} className={`p-3 border ${isMine(m) ? "border-[#2DD4A7]/40 bg-[#2DD4A7]/[0.05]" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{senderLabel(m)}</div>
                    <div className="text-[10px] font-mono text-[#8B97AE]">{(m.createdAt || "").slice(0, 19).replace("T", " ")}</div>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-white/10">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("support.thread.replyLabel")}</Label>
              <textarea data-testid="thread-reply-body" value={reply} onChange={(e) => setReply(e.target.value)} rows={3} className="mt-1 w-full rounded-none bg-transparent border border-white/20 text-sm p-3" placeholder={openThread.ticket.status === "closed" ? t("support.thread.replyPhClosed") : t("support.thread.replyPhOpen")} />
              <div className="flex items-center justify-end gap-2 mt-2">
                <Button data-testid="thread-reply-send" onClick={postReply} disabled={busy || !reply.trim()} className="rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] h-10 font-semibold disabled:opacity-40">{busy ? "…" : t("support.thread.send")}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
